import {
    FC,
    ReactElement,
    useEffect,
    useMemo,
} from 'react'

import { EmptyState } from '../../compositions'
import { ValkTalkPosts } from '../../containers/ValkTalkPosts/ValkTalkPosts'
import { ValkTalkFeedType, ValkTalkPost } from '../../entities/ValkTalkPost/ValkTalkPost'
import { filterValkTalkPostsBySearch, getPermittedFeedTypes } from '../../entities/ValkTalkPost/ValkTalkPostHelpers'
import { formatDate, parse } from '../../helpers/date'
import usePermissions from '../../helpers/hooks/usePermissions'
import trans from '../../helpers/trans'
import { useTypedDispatch, useTypedSelector } from '../../redux/store'
import { setToast } from '../../redux/toasts/toastsReducer'
import { deleteValkTalkPost, fetchValkTalkPosts } from '../../redux/valkTalkPosts/valkTalkPostsActions'
import {
    setError,
    setIsAddPostModalOpen,
    setMonth,
    setSearch,
} from '../../redux/valkTalkPosts/valkTalkPostsReducer'
import { ToastType } from '../../types'
import { DateFormat } from '../../types/dateFormatTypes'
import { useValkTalkFeedsClient } from '../ValkTalkFeedsClientProvider/ValkTalkFeedsClientProvider'

interface ConnectedValkTalkPostsProps {
    className?: string
}

export const ConnectedValkTalkPosts: FC<ConnectedValkTalkPostsProps> = ({ className = '' }): ReactElement => {
    const dispatch = useTypedDispatch()
    const permissions = usePermissions()
    const { client, userId, error: clientError } = useValkTalkFeedsClient()
    const {
        feedTypes,
        onlyOwnPosts,
        month,
        search,
        posts,
        hasMorePosts,
        isLoading,
        isDeleting,
        isAddPostModalOpen,
        error,
    } = useTypedSelector(state => state.valkTalkPostsReducer)

    const permittedFeedTypes = useMemo(() => getPermittedFeedTypes(permissions), [permissions])
    const monthDate = useMemo(() => parse(month, DateFormat.inputMonth, new Date()), [month])
    // No checked feed types means all feed types
    const selectedFeedTypes = feedTypes.length ? feedTypes : Object.values(ValkTalkFeedType)
    const authorId = onlyOwnPosts ? userId : undefined

    const searchedPosts = useMemo(() => filterValkTalkPostsBySearch(posts, search), [posts, search])

    const fetchPosts = (): void => {
        if (client) {
            dispatch(fetchValkTalkPosts(client, { feedTypes: selectedFeedTypes, month: monthDate, authorId }))
        }
    }

    useEffect(fetchPosts, [client, selectedFeedTypes.join(), monthDate, authorId])

    useEffect(() => () => {
        dispatch(setIsAddPostModalOpen(false))
    }, [])

    useEffect(() => {
        if (error) {
            dispatch(setToast({ text: error, type: ToastType.fail }))
            dispatch(setError(''))
        }
    }, [error])

    const handleMonthChange = (newMonth: Date): void => {
        dispatch(setMonth(formatDate(newMonth, DateFormat.inputMonth)))
    }

    const handleSearchChange = (value: string): void => {
        dispatch(setSearch(value))
    }

    const handleAddPostModalClose = (): void => {
        dispatch(setIsAddPostModalOpen(false))
    }

    const handleDeletePost = async (post: ValkTalkPost): Promise<boolean> => {
        if (! client) {
            return false
        }

        const isDeleted = await dispatch(deleteValkTalkPost(client, post.id))

        if (isDeleted) {
            dispatch(setToast({
                text: trans('containers.valkTalkPosts.deletePostSuccess'),
                type: ToastType.pass,
                shouldFadeOutAutomatically: true,
            }))
        }

        return isDeleted
    }

    if (clientError) {
        return (
            <EmptyState
                title={trans('containers.valkTalkPosts.errorTitle')}
                intro={clientError}
                className={className}
            />
        )
    }

    return (
        <ValkTalkPosts
            isLoading={isLoading}
            isDeleting={isDeleting}
            isAddPostModalOpen={isAddPostModalOpen}
            hasMorePosts={hasMorePosts}
            month={monthDate}
            search={search}
            posts={searchedPosts}
            permittedFeedTypes={permittedFeedTypes}
            onMonthChange={handleMonthChange}
            onSearchChange={handleSearchChange}
            onAddPostModalClose={handleAddPostModalClose}
            onPostSaved={fetchPosts}
            onDeletePost={handleDeletePost}
            className={className}
        />
    )
}
